import React from 'react';

import CarAiro from '../../img/car-airo.svg';
import CarAssociado from '../../img/car-associado.svg';
import CarAtual from '../../img/car-atual.svg';
import CarDiretoria from '../../img/car-diretoria.svg';
import CarExterno from '../../img/car-externo.svg';
import CarMobi from '../../img/car-mobi.svg';
import CarPerdaTotal from '../../img/car-perda-total.svg';
import CarTerceiro from '../../img/car-terceiro.svg';
import Moto from '../../img/moto.svg';

/**
 * Ícone da classe do veículo (at-75).
 *
 * A cadeia de `if`s que escolhe este ícone estava escrita por inteiro em **6 telas**
 * (Recepção, Chefe de Oficina, Tela de Controle, Orçamento, Estoque/Compras e Controle de
 * Liberação). Ao precisar dela numa sétima, foi extraída em vez de copiada de novo — mesmo
 * caminho do `Paginacao` na at-73.
 *
 * ⚠️ **As 6 telas NÃO foram migradas nesta entrega, de propósito**: são de uso diário, o
 * markup é idêntico e a verificação seria só visual — o mesmo motivo que deixou as duas
 * telas originais da paginação para depois. A substituição está registrada em
 * `docs/oficina/IDEIAS.md`.
 *
 * ## Por que este componente aceita DOIS formatos
 *
 * Medido em 18/08/2026: cinco das seis cópias comparam apenas o rótulo de exibição
 * (`'Associado'`), enquanto o **Controle de Liberação compara também o enum cru**
 * (`'ASSOCIADO'`) — 18 comparações em vez de 9. Uma das duas está errada e não há como
 * saber qual sem medir cada consumidor: ou aquelas cinco perdem o ícone quando a resposta
 * traz o enum, ou o Controle de Liberação carrega 9 ramos mortos.
 *
 * Este componente aceita os dois e normaliza, que é a única forma de estar certo nos dois
 * casos. Classe desconhecida (ou ausente) **não** renderiza ícone nenhum — nunca um
 * placeholder, que sugeriria uma classe que o veículo não tem.
 */

const ICONE_POR_CLASSE: Record<string, string> = {
  AIRO_CLUBE: CarAiro,
  ASSOCIADO: CarAssociado,
  CARROS_ATUAL: CarAtual,
  CARROS_EXTERNOS: CarExterno,
  DIRETORIA: CarDiretoria,
  MOBI: CarMobi,
  MOTO: Moto,
  PERDA_TOTAL: CarPerdaTotal,
  TERCEIRO: CarTerceiro,
};

/** "Carros Atual" e "CARROS_ATUAL" caem na mesma chave. */
const chaveDe = (classe: string): string =>
  classe.trim().toUpperCase().replace(/\s+/g, '_');

interface CarClassIconProps {
  classe?: string | null;
  /** Lado do ícone em px. As telas atuais usam 15. */
  tamanho?: number;
}

const CarClassIcon: React.FC<CarClassIconProps> = ({ classe, tamanho = 15 }) => {
  if (!classe) return null;

  const icone = ICONE_POR_CLASSE[chaveDe(classe)];
  if (!icone) return null;

  // `alt` vazio + aria-hidden: o rótulo da classe já está escrito ao lado, em texto. Repetir
  // no leitor de tela produziria "Associado Associado".
  return <img src={icone} alt="" aria-hidden="true" width={tamanho} height={tamanho} />;
};

export default CarClassIcon;
